import React, { forwardRef, Ref, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Modal from 'react-modal';
import { css } from 'emotion';
import Tracking from '@vromo/analytics';
import { Vehicle } from '@vromo/models';
import { Job } from '@vromo/models';
import { when } from 'mobx';
import { fadeIn, jackInTheBox } from '@vromo/ui/styles/animated';
import Color from 'color';
import { serialize } from 'serializr';
import isValidHttpUrl from '@vromo/utils/isValidUrl';
import Bugsnag from '@vromo/analytics/bugsnag';

const prependHttp = (url: string) => (url.startsWith('http') ? url : `http://${url}`);
interface WidgetProps {
    widget: any;
    theme: any;
    vehicle: Vehicle;
    job: Job;
}

const Popup = forwardRef(function Popup(
    { widget, theme, job }: WidgetProps,
    ref: Ref<HTMLDivElement>
) {
    const [closed, setClosed] = useState(false);
    const [open, setOpen] = useState(false);
    const [visible, setVisible] = useState(!widget.metadata.verificationUrl);

    const [submitted, setSubmitted] = useState(false);

    const newVerificationUrl = prependHttp(widget.metadata.verificationUrl);

    useEffect(() => {
        if (isValidHttpUrl(prependHttp(newVerificationUrl))) {
            const verificationUrl = new URL(newVerificationUrl);

            Object.entries(serialize(job.contact)).forEach(([key, value]) => {
                verificationUrl.searchParams.set(key, value);
            });

            fetch(verificationUrl.href)
                .then((response) => {
                    if (response.ok) {
                        response.json().then((success) => {
                            setVisible(success !== true);
                        });
                    }
                })
                .catch(Bugsnag.notify);
        }
    }, []);

    function subscribe() {
        Tracking.track('OptIn Widget Link Opened', {
            widget: widget.id
        });

        fetch(widget.content, {
            method: 'POST',
            body: JSON.stringify({
                at: Date.now(),
                job: {
                    contact: serialize(job.contact),
                    ...(job.site && {
                        site: {
                            id: job.site.id,
                            ...(job.site.contact && {
                                contact: {
                                    name: job.site.contact.name
                                }
                            })
                        }
                    }),
                    name: job.name
                }
            })
        });

        setSubmitted(true);
    }

    useEffect(() => {
        return when(
            () => {
                switch (widget.settings.when) {
                    case 'dispatched':
                        return job.status === 'dispatched';
                    case 'underway':
                        return job.status === 'underway' && job.progress === 0;
                    case 'completed':
                        return job.status === 'completed';
                    case 'cancelled':
                        return job.status === 'cancelled';
                    case 'task0':
                        return job.progress === 1 && job.status === 'underway';
                    case 'task1':
                        return job.progress === 2 && job.status === 'underway';
                    case 'task2':
                        return job.progress === 3 && job.status === 'underway';
                    case 'task3':
                        return job.progress === 4 && job.status === 'underway';
                    default:
                        return false;
                }
            },
            () => {
                setOpen(true);
            }
        );
    }, []);

    return (
        <Modal
            isOpen={open && !closed && visible}
            className={css`
                animation: ${jackInTheBox} 1s ease-in;
            `}
            style={{
                content: {
                    backgroundColor: theme.background,
                    overflow: 'visible',
                    borderRadius: 34,
                    border: 'none',
                    padding: 0,
                    top: 'initial',
                    bottom: 'initial',
                    left: 'initial',
                    right: 'initial',
                    maxWidth: 450,
                    position: 'relative',
                    outline: 'none'
                },
                overlay: {
                    backgroundColor: 'rgba(0,0,0,0.32)',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 20
                }
            }}>
            <div style={{ padding: '0 24px', textAlign: 'center' }}>
                <h2
                    className={css`
                        text-align: center;
                        font-size: 22px;
                        font-weight: 500;
                        color: ${Color(theme.background).isLight() ? '#474747' : '#EFEFEF'};
                        margin: 8px;
                        margin-top: 32px;

                        @media (max-height: 500px) {
                            font-size: 20px;
                        }
                    `}>
                    {widget.metadata.title}
                </h2>
                {submitted ? (
                    <div>
                        <FontAwesome
                            icon="check"
                            style={{
                                fontSize: 30,
                                marginBottom: 16,
                                color: Color(theme.background).isLight() ? '#474747' : '#EFEFEF'
                            }}
                        />
                    </div>
                ) : (
                    <button
                        onClick={subscribe}
                        className={css`
                            background-color: #70a400; // ${theme.accent};
                            border-radius: 1000px;
                            padding: 6px 50px;
                            font-size: 18px;
                            color: ${theme.background};
                            border: none;
                            margin: 8px 0;
                            margin-bottom: 16px;

                            outline: none;
                            cursor: pointer;

                            @media (max-height: 500px) {
                                font-size: 16px;
                            }
                        `}>
                        {widget.metadata.callToAction || 'Subscribe'}
                    </button>
                )}
                <div
                    style={{
                        fontSize: 10,
                        color: Color(theme.background).isLight() ? '#474747' : '#EFEFEF',
                        marginBottom: 16
                    }}>
                    By clicking {widget.metadata.callToAction || 'Subscribe'} you agree to provide
                    your name, email address, and phone number to{' '}
                    {widget.metadata.description || 'a third party'} in accordance with the{' '}
                    <a target="_blank" href={widget.metadata.link}>
                        terms of service
                    </a>
                </div>
            </div>
            <div
                className={css`
                    opacity: 0;
                    animation: ${fadeIn} 1.2s ease-in;
                    animation-delay: 2s;
                    animation-fill-mode: both;
                `}
                onClick={() => setClosed(true)}
                style={{
                    display: 'flex',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    width: 30,
                    height: 30,
                    zIndex: 1000,
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                }}>
                <FontAwesome icon="times" weight="light" style={{ fontSize: 18 }} />
            </div>
        </Modal>
    );
});

const Inline = forwardRef(function Inline(
    { widget, theme, vehicle, job }: WidgetProps,
    ref: Ref<HTMLDivElement>
) {
    const [visible, setVisible] = useState(!widget.metadata.verificationUrl);
    const [submitted, setSubmitted] = useState(false);

    const newVerificationUrl = prependHttp(widget.metadata.verificationUrl);

    useEffect(() => {
        if (isValidHttpUrl(newVerificationUrl)) {
            const verificationUrl = new URL(newVerificationUrl);

            Object.entries(serialize(job.contact)).forEach(([key, value]) => {
                verificationUrl.searchParams.set(key, value);
            });
            fetch(verificationUrl.href)
                .then((response) => {
                    if (response.ok) {
                        response.json().then((success) => {
                            setVisible(success !== true);
                        });
                    }
                })
                .catch(Bugsnag.notify);
        }
    }, []);

    function subscribe() {
        Tracking.track('OptIn Widget Link Opened', {
            widget: widget.id
        });

        fetch(widget.content, {
            method: 'POST',
            body: JSON.stringify({
                at: Date.now(),
                job: {
                    contact: serialize(job.contact),
                    ...(job.site && {
                        site: {
                            id: job.site.id,
                            ...(job.site.contact && {
                                contact: {
                                    name: job.site.contact.name
                                }
                            })
                        }
                    }),
                    name: job.name
                }
            })
        });

        setSubmitted(true);
    }

    if (!visible) return null;

    return (
        <div
            ref={ref}
            className={css`
                margin-bottom: 8px;
                padding: 12px 12px;
                text-align: center;
            `}>
            <h2
                className={css`
                    text-align: center;
                    font-size: 20px;
                    font-weight: 500;
                    color: ${Color(theme.background).isLight() ? '#474747' : '#EFEFEF'};
                    margin: 8px;
                    margin-top: 32px;

                    @media (max-height: 500px) {
                        font-size: 18px;
                    }
                `}>
                {widget.metadata.title}
            </h2>
            {submitted ? (
                <div>
                    <FontAwesome
                        icon="check"
                        style={{
                            fontSize: 30,
                            marginBottom: 16,
                            color: Color(theme.background).isLight() ? '#474747' : '#EFEFEF'
                        }}
                    />
                </div>
            ) : (
                <button
                    onClick={subscribe}
                    className={css`
                        background-color: #70a400; // ${theme.accent};
                        border-radius: 1000px;
                        padding: 6px 50px;
                        font-size: 18px;
                        color: ${theme.background};
                        border: none;
                        margin: 8px 0;
                        margin-bottom: 16px;

                        outline: none;
                        cursor: pointer;

                        @media (max-height: 500px) {
                            font-size: 16px;
                        }
                    `}>
                    {widget.metadata.callToAction || 'Subscribe'}
                </button>
            )}
            <div
                style={{
                    fontSize: 10,
                    color: Color(theme.background).isLight() ? '#474747' : '#EFEFEF',
                    marginBottom: 16
                }}>
                By clicking {widget.metadata.callToAction || 'Subscribe'} you agree to provide your
                name, email address, and phone number to{' '}
                {widget.metadata.description || 'a third party'} in accordance with the{' '}
                <a target="_blank" href={widget.metadata.link}>
                    terms of service
                </a>
            </div>
        </div>
    );
});

export default observer(
    function OptIn({ widget, theme, vehicle, job }: WidgetProps, ref: Ref<HTMLDivElement>) {
        const OptInComponent = widget.settings && widget.settings.popup ? Popup : Inline;

        return (
            <OptInComponent ref={ref} widget={widget} theme={theme} vehicle={vehicle} job={job} />
        );
    },
    {
        forwardRef: true
    }
);
